/**
 * Exploration Map Component
 *
 * Interactive map for exploring and studying GeoJSON datasets.
 * Shows feature labels using polylabel (pole of inaccessibility) positioning.
 * Designed for pre-quiz exploration and dataset preview.
 *
 * Features:
 * - Always-visible labels (not just hover)
 * - Polylabel positioning for optimal placement
 * - Property selector for choosing which labels to display
 * - Interactive hover effects
 * - Feature grouping by label value
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Position,
} from "geojson";
import type { LeafletMouseEvent } from "leaflet";
import L from "leaflet";
import polylabel from "polylabel";
import { BaseMap } from "./BaseMap";

interface ExplorationMapProps {
  data: FeatureCollection<Geometry, GeoJsonProperties>;
  labelProperty: string | null;
  showLabels?: boolean; // Toggle labels visibility
  className?: string;
  mapHeight?: number | string;
  mapStyle?: "light" | "dark" | "voyager" | "satellite" | "osm";
}

type LayerWithFeature = L.Path & {
  feature?: Feature<Geometry, GeoJsonProperties>;
};

/**
 * Find the optimal label position for a feature using pole of inaccessibility algorithm.
 * This finds the point within the polygon that is furthest from any edge.
 * Falls back to bounds center if the geometry is unsupported.
 */
function findLabelPosition(
  feature: Feature,
  bounds: L.LatLngBounds,
): L.LatLng {
  const geometry = feature.geometry;

  // Helper to convert GeoJSON coordinates to polylabel format
  // GeoJSON uses [longitude, latitude] which maps to [x, y]
  // Position is [lon, lat] or [lon, lat, elevation] - we take first two values
  const convertRing = (ring: Position[]): [number, number][] => {
    return ring.map((pos) => [pos[0] ?? 0, pos[1] ?? 0]);
  };

  try {
    if (geometry.type === "Polygon") {
      // Polygon has rings: exterior ring + holes
      const rings = geometry.coordinates.map(convertRing);
      // Use precision of 0.01 for geographic coordinates
      const pole = polylabel(rings, 0.01);
      // polylabel always returns [x, y] (lon, lat), we need [lat, lon] for Leaflet
      return L.latLng(pole[1]!, pole[0]!);
    } else if (geometry.type === "MultiPolygon") {
      // For MultiPolygon, find the pole of the largest polygon by area
      let largestPolygonRings: [number, number][][] | null = null;
      let largestArea = 0;

      geometry.coordinates.forEach((polygonCoords) => {
        // Get the exterior ring (first ring)
        const exteriorRing = polygonCoords[0];
        if (!exteriorRing) return;

        const polygon = convertRing(exteriorRing);
        // Simple area approximation (good enough for comparison)
        const area = Math.abs(
          polygon.reduce((sum, [x, y], i, arr) => {
            const [nextX, nextY] = arr[(i + 1) % arr.length] ?? [0, 0];
            return sum + (x * nextY - nextX * y);
          }, 0) / 2,
        );

        if (area > largestArea) {
          largestArea = area;
          largestPolygonRings = polygonCoords.map(convertRing);
        }
      });

      if (largestPolygonRings) {
        // Use precision of 0.01 for geographic coordinates
        const pole = polylabel(largestPolygonRings, 0.01);
        // polylabel always returns [x, y] (lon, lat), we need [lat, lon] for Leaflet
        return L.latLng(pole[1]!, pole[0]!);
      }
    }
  } catch (error) {
    // If polylabel fails for any reason, fall back to center
    console.warn("Polylabel failed, using bounds center:", error);
  }

  // Fallback: use bounds center for other geometry types or if polylabel fails
  return bounds.getCenter();
}

/**
 * Extract display value from a feature property
 */
function getPropertyValue(
  feature: Feature<Geometry, GeoJsonProperties>,
  propertyKey: string,
): string {
  const value = feature.properties?.[propertyKey];

  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    const stringValues = value
      .filter((v) => v !== null && v !== undefined)
      .map(String);
    return stringValues.join(", ");
  }

  return String(value);
}

/**
 * ExplorationMap - Interactive map with always-visible labels for dataset exploration
 */
export function ExplorationMap({
  data,
  labelProperty,
  showLabels = true,
  className,
  mapHeight,
  mapStyle = "voyager",
}: ExplorationMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);
  const labelsRef = useRef<L.LayerGroup | null>(null);
  const labelMapRef = useRef<Map<string, L.Marker>>(new Map());
  const featureGroupMapRef = useRef<Map<string, string[]>>(new Map());

  console.log("🗺️ ExplorationMap render:", {
    featuresCount: data?.features.length ?? 0,
    labelProperty,
    showLabels,
  });

  // Create labels for all features (V1-inspired - no layerReady flag needed)
  useEffect(() => {
    console.log("🏷️ Label creation effect triggered:", {
      hasMap: !!mapRef.current,
      hasLayer: !!layerRef.current,
      labelProperty,
      showLabels,
    });

    if (!mapRef.current || !layerRef.current || !labelProperty || !showLabels) {
      console.log("⚠️ Skipping label creation - conditions not met");
      // Clear labels if no property selected or labels are hidden
      if (labelsRef.current && mapRef.current) {
        mapRef.current.removeLayer(labelsRef.current);
        labelsRef.current = null;
      }
      return;
    }

    // Wait for layers to be fully rendered before creating labels
    const timeoutId = window.setTimeout(() => {
      if (!mapRef.current || !layerRef.current) return;

      // Clear existing labels
      if (labelsRef.current && mapRef.current) {
        mapRef.current.removeLayer(labelsRef.current);
      }

      // Clear label map and group map
      labelMapRef.current.clear();
      featureGroupMapRef.current.clear();

      // Group features by their label value
      type FeatureGroup = {
        labelText: string;
        featureIds: string[];
        centers: L.LatLng[];
      };
      const featureGroups = new Map<string, FeatureGroup>();

      let featureIndex = 0;
      layerRef.current.eachLayer((layer) => {
        const typedLayer = layer as LayerWithFeature & {
          getBounds?: () => L.LatLngBounds;
        };
        if (!typedLayer.feature) {
          return;
        }

        // Generate unique ID: use feature.id if available, otherwise use index
        const featureId = typedLayer.feature.id !== undefined
          ? String(typedLayer.feature.id)
          : `feature-${featureIndex}`;

        // Store the ID on the layer for later retrieval
        (typedLayer as any)._featureId = featureId;

        featureIndex++;

        const labelText = getPropertyValue(typedLayer.feature, labelProperty);

        if (!labelText) {
          return;
        }

        // Get the center of the feature
        const bounds = typedLayer.getBounds?.();
        if (!bounds || !bounds.isValid()) {
          return;
        }

        const center = findLabelPosition(typedLayer.feature, bounds);

        // Add to group
        if (!featureGroups.has(labelText)) {
          featureGroups.set(labelText, {
            labelText,
            featureIds: [],
            centers: [],
          });
        }
        const group = featureGroups.get(labelText)!;
        group.featureIds.push(featureId);
        group.centers.push(center);
      });

      // Create new label layer with one label per group
      const labelGroup = L.layerGroup();

      featureGroups.forEach((group) => {
        // Calculate centroid of all feature centers in the group
        const avgLat = group.centers.reduce((sum, c) => sum + c.lat, 0) / group.centers.length;
        const avgLng = group.centers.reduce((sum, c) => sum + c.lng, 0) / group.centers.length;
        const centroid = L.latLng(avgLat, avgLng);

        // Create a single label for all features in this group
        const escapedLabel = group.labelText.replace(/"/g, '&quot;');
        const label = L.marker(centroid, {
          icon: L.divIcon({
            className: "map-label",
            html: `<div class="label-content" data-group="${escapedLabel}" style="
              background: rgba(255, 255, 255, 0.95);
              border: 1px solid rgba(100, 116, 139, 0.3);
              border-radius: 6px;
              padding: 4px 8px;
              font-size: 11px;
              font-weight: 600;
              color: rgb(15, 23, 42);
              white-space: nowrap;
              text-align: center;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              pointer-events: none;
              user-select: none;
              transform: translate(-50%, -50%);
              transition: all 0.15s ease;
            ">${escapedLabel}</div>`,
            iconSize: undefined,
            iconAnchor: [0, 0],
          }),
          interactive: false,
        });

        labelGroup.addLayer(label);

        // Map all feature IDs in the group to this single label
        // Also create bidirectional mapping for group highlighting
        group.featureIds.forEach((featureId) => {
          labelMapRef.current.set(featureId, label);
          featureGroupMapRef.current.set(featureId, group.featureIds);
        });
      });

      labelGroup.addTo(mapRef.current);
      labelsRef.current = labelGroup;

      console.log(`✅ Created ${featureGroups.size} label groups on map`);
    }, 150); // Small delay to ensure layers are rendered

    return () => {
      window.clearTimeout(timeoutId);
      if (labelsRef.current && mapRef.current) {
        mapRef.current.removeLayer(labelsRef.current);
        labelsRef.current = null;
      }
    };
  }, [data, labelProperty, showLabels]);

  // Handle feature mouseover - just highlight the corresponding label
  // BaseMap handles feature hover styling automatically
  const handleFeatureMouseOver = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, event: LeafletMouseEvent) => {
      // Get feature ID from the layer
      const featureId = (event.target as any)._featureId ?? String(feature?.id ?? "");

      // Highlight the corresponding label
      const label = labelMapRef.current.get(featureId);
      if (label) {
        const element = label.getElement();
        const labelDiv = element?.querySelector(".label-content");
        if (labelDiv instanceof HTMLElement) {
          labelDiv.style.background = "rgba(59, 130, 246, 0.95)";
          labelDiv.style.color = "rgb(255, 255, 255)";
          labelDiv.style.borderColor = "rgba(59, 130, 246, 0.6)";
          labelDiv.style.transform = "translate(-50%, -50%) scale(1.05)";
        }
      }
    },
    [],
  );

  // Handle feature mouseout - reset the label
  // BaseMap handles feature style reset automatically
  const handleFeatureMouseOut = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, event: LeafletMouseEvent) => {
      // Get feature ID from the layer
      const featureId = (event.target as any)._featureId ?? String(feature?.id ?? "");

      // Reset the label style
      const label = labelMapRef.current.get(featureId);
      if (label) {
        const element = label.getElement();
        const labelDiv = element?.querySelector(".label-content");
        if (labelDiv instanceof HTMLElement) {
          labelDiv.style.background = "rgba(255, 255, 255, 0.95)";
          labelDiv.style.color = "rgb(15, 23, 42)";
          labelDiv.style.borderColor = "rgba(100, 116, 139, 0.3)";
          labelDiv.style.transform = "translate(-50%, -50%) scale(1)";
        }
      }
    },
    [],
  );

  // Render tooltip content - show ALL feature properties (V2 Enhanced)
  // Highlight the prompted property (labelProperty) for easy identification
  const renderTooltipContent = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>) => {
      const properties = feature.properties ?? {};
      console.log("🖱️ Rendering tooltip for feature with properties:", Object.keys(properties));

      return (
        <div className="space-y-1 max-h-96 overflow-y-auto">
          <div className="space-y-0.5 text-xs">
            {Object.entries(properties)
              .filter(([_, value]) => value !== null && value !== undefined)
              .map(([key, value]) => {
                // Check if this is the prompted property
                const isPrompted = labelProperty && key === labelProperty;

                return (
                  <div
                    key={key}
                    className={`flex justify-between gap-4 ${
                      isPrompted
                        ? "bg-blue-50 dark:bg-blue-950/30 -mx-1 px-1 py-0.5 rounded"
                        : ""
                    }`}
                  >
                    <span
                      className={`font-medium ${
                        isPrompted
                          ? "text-blue-700 dark:text-blue-400 font-semibold"
                          : "text-foreground/70"
                      }`}
                    >
                      {key}:
                    </span>
                    <span
                      className={`text-right ${
                        isPrompted
                          ? "text-blue-900 dark:text-blue-300 font-semibold"
                          : "text-foreground/80"
                      }`}
                    >
                      {String(value).length > 40
                        ? String(value).slice(0, 40) + "..."
                        : String(value)}
                    </span>
                  </div>
                );
              })}
          </div>
          {Object.keys(properties).length > 10 && (
            <div className="text-[10px] text-muted-foreground pt-1 text-center border-t">
              Scroll to see all {Object.keys(properties).length} properties
            </div>
          )}
        </div>
      );
    },
    [labelProperty],
  );

  return (
    <BaseMap
      data={data}
      className={className}
      mapHeight={mapHeight ?? 520}
      mapStyle={mapStyle}
      hideLabels={false}
      // Use semantic state system - all features in "default" state
      // BaseMap will apply consistent styling automatically
      getFeatureState={() => "default"}
      enableHoverEffect={true}
      onFeatureMouseOver={handleFeatureMouseOver}
      onFeatureMouseOut={handleFeatureMouseOut}
      showTooltip={true}
      renderTooltipContent={renderTooltipContent}
      onMapReady={(map) => {
        mapRef.current = map;
      }}
      onLayerReady={(layer) => {
        layerRef.current = layer;
      }}
    />
  );
}
